export default {
  id: 'stock-explanations',
  title: 'Stock Explanations',
  content: `## Stock Explanations

For each stock in the Analysis Mode screener, Nyx generates a structured AI explanation that goes beyond the numbers. It tells you in plain English why the stock is ranked where it is and what you would need to see for a trade to make sense.

---

## How to Access

**Nyx AI → Analysis tab → tap any stock in the ranked list**

The explanation loads in a panel or expanded card below the stock's scores.

---

## The Three Components of Every Explanation

---

### Reason

The "why" behind the ranking. Nyx synthesises the technical, volume, and sentiment signals into a plain-English narrative.

**Example:**
> *RELIANCE is ranked #3 today. It broke above its 20-day high yesterday on 2.4× average volume and has since been consolidating tightly just above the breakout level. RSI at 62 suggests momentum without extension. News sentiment is mildly positive from refinery output coverage. All four signal components are aligned.*

---

### Trigger

The specific condition that would confirm an entry — what you would need to see on the chart or in the data before taking a trade.

**Example:**
> *A fresh close above ₹2,910 with volume > 1.5× average would confirm continuation of the breakout. Alternatively, a clean hold above the breakout level on a volume contraction day (healthy consolidation) followed by an expansion day.*

---

### Invalidation

The condition that means the original thesis is wrong and you should exit or not enter.

**Example:**
> *If price closes back below ₹2,870 (the breakout level), the breakout is likely false. At that point, the setup no longer applies — do not hold expecting a recovery.*

The invalidation level is your stop loss reference. It tells you where the trade is wrong, which tells you where to place your stop.

---

## Explanation Caching

Explanations are generated by AI and cached for **1 hour** per stock per day. This means:
- The first person to open a stock's explanation triggers the generation (may take a few seconds)
- Subsequent views within 1 hour load instantly from cache
- Explanations refresh after 1 hour to reflect updated market conditions

---

## How to Use Explanations

**Step 1:** Check the Reason — does the narrative match what you see on the chart?

**Step 2:** Check the Trigger — is the triggering condition close to happening, or has it already happened (in which case you may be late)?

**Step 3:** Check the Invalidation — use this as your stop reference. Does the invalidation level give you a viable R:R with your target?

**Step 4:** If all three make sense, use the trade plan as a starting point in ACT or the Trade page — do not just copy it blindly.

---

## Explanation Quality

The quality of an explanation depends on the quality of the underlying signals. Stocks in the top 10 of the ranked list, with multiple scanner signals fired and strong volume, produce the most specific and reliable explanations.

For stocks ranked 40–51 with weak scores, the explanation may reflect a lack of conviction — and that is informative too. A weak explanation on a weakly-ranked stock is a signal to move on.`,
};
